import { CloseIcon, HamburgerIcon } from '@chakra-ui/icons'
import {
  Box,
  Collapse,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Link,
  Popover,
  PopoverTrigger,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { NAV_ITEMS, NavItem } from '../global'
import { useTranslation } from 'react-i18next'

export default function Navigation() {
  const { isOpen, onToggle } = useDisclosure()
  const headingColor = useColorModeValue('purple.600', 'purple.300')
  const { t } = useTranslation()

  return (
    <Box as="nav" position="sticky" top={0} zIndex={99}>
      <Flex
        bg={useColorModeValue('white', 'gray.800')}
        color={'purple.500'}
        minH={'60px'}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
        justifyContent={'space-between'}
      >
        <Flex justifyContent={'space-between'} flex={1}>
          <Heading as="h1" size="lg" letterSpacing={'tighter'}>
            <Link
              as={RouterLink}
              to="/"
              _hover={{ textDecoration: 'none' }}
              color={'purple.500'}
            >
              <HStack>
                <span>{t('nav.brand')}</span>
              </HStack>
            </Link>
          </Heading>

          <Flex display={{ base: 'none', md: 'flex' }} ml={10}>
            <DesktopNav />
          </Flex>
        </Flex>

        <Flex ml={{ base: -2 }} display={{ base: 'flex', md: 'none' }}>
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant={'ghost'}
            color="purple.500"
            aria-label={t('nav.toggleNav')}
          />
        </Flex>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav onToggle={onToggle} />
      </Collapse>
    </Box>
  )
}

const DesktopNav = () => {
  return (
    <Stack direction={'row'} spacing={4} alignItems="center">
      {NAV_ITEMS.map((navItem) => (
        <Box key={navItem.label}>
          <Popover trigger={'hover'} placement={'bottom-start'}>
            <PopoverTrigger>
              <Box
                as={RouterLink}
                p={2}
                to={navItem.href ?? '#'}
                fontSize={'md'}
                fontWeight={600}
                color={'purple.500'}
                _hover={{
                  textDecoration: 'none',
                  color: 'purple.700',
                }}
              >
                {navItem.label}
              </Box>
            </PopoverTrigger>
          </Popover>
        </Box>
      ))}
    </Stack>
  )
}

const MobileNav = ({ onToggle }: { onToggle: () => void }) => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={4}
      display={{ md: 'none' }}
      borderBottom={1}
      borderStyle={'solid'}
      borderColor={useColorModeValue('gray.200', 'gray.900')}
    >
      {NAV_ITEMS.map((navItem) => (
        <MobileNavItem key={navItem.label} {...navItem} onToggle={onToggle} />
      ))}
    </Stack>
  )
}

const MobileNavItem = ({ label, children, href, onToggle }: NavItem) => {
  return (
    <Stack spacing={4} onClick={onToggle}>
      <Box
        py={2}
        as={RouterLink}
        to={href ?? '#'}
        justifyContent="space-between"
        alignItems="center"
        _hover={{
          textDecoration: 'none',
        }}
      >
        <Text fontWeight={600} color={'purple.500'}>
          {label}
        </Text>
      </Box>
    </Stack>
  )
}
